import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { threadAPI, authAPI, commentAPI, reportAPI } from '../lib/api';
import { Navbar } from '../components/Navbar';
import { VoteButtons } from '../components/VoteButtons';
import { CommentSection } from '../components/CommentSection';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { 
  ArrowLeft, Calendar, Trash2, Edit, Bookmark, Flag, Pin, Lock, 
  CheckCircle, MessageSquare, Eye, Loader2
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';

export default function ThreadDetail() {
  const { threadId } = useParams();
  const navigate = useNavigate();
  const [thread, setThread] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    fetchThread();
    fetchCurrentUser();
  }, [threadId]);

  const fetchThread = async () => {
    try {
      const response = await threadAPI.getOne(threadId);
      setThread(response.data);
    } catch (error) {
      toast.error('Thread not found');
      navigate('/forum');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCurrentUser = async () => {
    try {
      const response = await authAPI.getMe();
      setCurrentUser(response.data);
    } catch (error) {
      // Not logged in
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await threadAPI.delete(threadId);
      toast.success('Thread deleted');
      navigate('/forum');
    } catch (error) {
      toast.error('Failed to delete thread');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleBookmark = async () => {
    if (!currentUser) {
      toast.error('Please sign in to bookmark');
      return;
    }
    try {
      await threadAPI.bookmark(threadId);
      toast.success('Bookmark updated');
    } catch (error) {
      toast.error('Failed to update bookmark');
    }
  };

  const handleReport = async () => {
    if (!currentUser) {
      toast.error('Please sign in to report');
      return;
    }
    try {
      await reportAPI.create({
        target_type: 'thread',
        target_id: threadId,
        reason: 'inappropriate',
      });
      toast.success('Report submitted');
    } catch (error) {
      toast.error('Failed to submit report');
    }
  };

  const isOwner = currentUser?.user_id === thread?.author_id;
  const isAdmin = currentUser?.role === 'admin';

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
        </div>
      </div>
    );
  }

  if (!thread) return null;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="thread-detail-page">
      <Navbar />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <Link to="/forum" className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6">
          <ArrowLeft className="w-4 h-4" />
          Back to Forum
        </Link>

        {/* Thread Content */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mb-6">
          <div className="flex">
            {/* Vote Column */}
            <div className="flex flex-col items-center justify-start px-4 py-6 bg-slate-50 border-r border-slate-200">
              <VoteButtons
                targetType="thread"
                targetId={thread.thread_id}
                initialUpvotes={thread.upvotes}
                initialDownvotes={thread.downvotes}
                userVote={thread.user_vote}
              />
            </div>

            {/* Content */}
            <div className="flex-1 p-6 sm:p-8">
              {/* Status badges */}
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <Badge variant="secondary">{thread.category}</Badge>
                {thread.is_pinned && (
                  <Badge className="bg-amber-100 text-amber-700">
                    <Pin className="w-3 h-3 mr-1" />Pinned
                  </Badge>
                )}
                {thread.is_locked && (
                  <Badge className="bg-red-100 text-red-700">
                    <Lock className="w-3 h-3 mr-1" />Locked
                  </Badge>
                )}
                {thread.is_solved && (
                  <Badge className="bg-green-100 text-green-700">
                    <CheckCircle className="w-3 h-3 mr-1" />Solved
                  </Badge>
                )}
              </div>

              {/* Author info */}
              <div className="flex items-center gap-3 mb-4">
                <Link to={`/users/${thread.author_id}`} className="flex items-center gap-2 hover:opacity-80">
                  <Avatar className="w-10 h-10">
                    <AvatarImage src={thread.author?.picture || thread.author_picture} />
                    <AvatarFallback className="bg-emerald-100 text-emerald-700">
                      {thread.author_name?.charAt(0)?.toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <span className="font-medium text-slate-900">{thread.author_name}</span>
                    {thread.author?.level && (
                      <Badge variant="secondary" className="ml-2 text-xs">
                        {thread.author.level.name}
                      </Badge>
                    )}
                  </div>
                </Link>
                <span className="text-slate-400">•</span>
                <span className="flex items-center gap-1 text-sm text-slate-500">
                  <Calendar className="w-4 h-4" />
                  {formatDistanceToNow(new Date(thread.created_at), { addSuffix: true })}
                </span>
                <span className="flex items-center gap-1 text-sm text-slate-500">
                  <Eye className="w-4 h-4" />
                  {thread.views || 0} views
                </span>
              </div>

              {/* Title */}
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4" data-testid="thread-title">
                {thread.title}
              </h1>

              {/* Tags */}
              {thread.tags?.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-6">
                  {thread.tags.map((tag) => (
                    <Badge key={tag} variant="outline">
                      {tag}
                    </Badge>
                  ))}
                </div>
              )}

              {/* Content */}
              <div className="prose-content text-slate-700 mb-6">
                <p className="whitespace-pre-wrap" data-testid="thread-content">{thread.content}</p>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap items-center gap-2 pt-4 border-t border-slate-200">
                <Button variant="ghost" size="sm" onClick={handleBookmark}>
                  <Bookmark className="w-4 h-4 mr-2" />
                  Bookmark
                </Button>
                <Button variant="ghost" size="sm" onClick={handleReport}>
                  <Flag className="w-4 h-4 mr-2" />
                  Report
                </Button>
                
                {isOwner && (
                  <>
                    <Button variant="ghost" size="sm" onClick={() => navigate(`/forum/thread/${threadId}/edit`)}>
                      <Edit className="w-4 h-4 mr-2" />
                      Edit
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="sm" className="text-red-600">
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete Thread?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This action cannot be undone. All replies will be deleted.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={handleDelete}
                            disabled={isDeleting}
                            className="bg-red-600 hover:bg-red-700"
                          >
                            {isDeleting ? 'Deleting...' : 'Delete'}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Comments */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 sm:p-8">
          {thread.is_locked ? (
            <div className="text-center py-8 text-slate-500">
              <Lock className="w-8 h-8 mx-auto mb-2" />
              <p>This thread is locked. No new replies can be added.</p>
            </div>
          ) : (
            <CommentSection
              targetType="thread"
              targetId={threadId}
              currentUser={currentUser}
            />
          )}
        </div>
      </main>
    </div>
  );
}
